/* eslint-disable */
import { db as dbInstance, ensureFirebase } from "services/firebase"; // ✅ 각 프로젝트 기준으로 수정
import {
    doc,
    getDoc,
    updateDoc,
    serverTimestamp,
    runTransaction,
    collection,
    getDocs,
    query,
    where,
    limit,
} from "firebase/firestore";

const nowTs = () => serverTimestamp();
const USERS = "users"; // ✅ 명시적 컬렉션명

function getDb() {
    return dbInstance || ensureFirebase().db;
}

function merge(obj = {}, patch = {}) {
    return { ...(obj || {}), ...(patch || {}) };
}

/** 사용자 문서 조회 */
export async function getUser(uid) {
    const db = getDb();
    const snap = await getDoc(doc(collection(db, USERS), uid));
    return snap.exists() ? snap.data() : null;
}

/**
 * 로그인 직후 최소 초기 세팅
 * - phoneE164 항상 저장
 * - 반환: { isNew: boolean }
 */
export async function ensureUserDoc(
    uid,
    {
        provider = "phone",
        phoneE164 = null,
        email = null,
        displayName = null,
        loginMethodToSet = null,
        passVerified = false,
    } = {}
) {
    const db = getDb();
    const colUsers = collection(db, USERS);
    let isNew = false;

    await runTransaction(db, async (tx) => {
        const uref = doc(colUsers, uid);
        const usnap = await tx.get(uref);

        if (!usnap.exists()) {
            const base = {
                uid,
                provider,
                loginMethods: {},
                createdAt: nowTs(),
                updatedAt: nowTs(),
                lastLoginAt: nowTs(),
                active: true,
                profile: {},
            };
            if (phoneE164) {
                base.phoneE164 = phoneE164;
                base.phoneVerifiedAt = nowTs();
            }
            if (passVerified) base.passVerifiedAt = nowTs();
            if (email) base.email = email;
            if (displayName) base.profile.displayName = displayName;
            if (loginMethodToSet) base.loginMethods[loginMethodToSet] = true;

            tx.set(uref, base);
            isNew = true;
        } else {
            const cur = usnap.data() || {};
            const patch = {
                updatedAt: nowTs(),
                lastLoginAt: nowTs(),
                loginMethods: { ...(cur.loginMethods || {}) },
                profile: { ...(cur.profile || {}) },
            };
            if (loginMethodToSet) patch.loginMethods[loginMethodToSet] = true;
            if (displayName && !patch.profile.displayName) patch.profile.displayName = displayName;
            if (email && !cur.email) patch.email = email;
            if (phoneE164) {
                patch.phoneE164 = phoneE164;
                if (!cur.phoneVerifiedAt) patch.phoneVerifiedAt = nowTs();
            }
            if (passVerified && !cur.passVerifiedAt) patch.passVerifiedAt = nowTs();

            tx.update(uref, patch);
            isNew = false;
        }
    });

    return { isNew };
}

/** 부분 업데이트(얕은) — 프로젝트 전용 필드 확장에 사용 */
export async function updateUser(uid, patch) {
    const db = getDb();
    const ref = doc(collection(db, USERS), uid);
    await updateDoc(ref, merge(patch, { updatedAt: nowTs() }));
    return true;
}

/** 약관 동의 저장 */
export async function setAgreements(uid, { tos, privacy, marketing = false, version = "v1" }) {
    const db = getDb();
    const ref = doc(collection(db, USERS), uid);
    await updateDoc(ref, {
        agreements: {
            tos: !!tos,
            privacy: !!privacy,
            marketing: !!marketing,
            version,
            timestamp: nowTs(),
        },
        updatedAt: nowTs(),
    });
    return true;
}

/** 표시 이름 저장 */
export async function setDisplayName(uid, displayName) {
    const db = getDb();
    const ref = doc(collection(db, USERS), uid);
    await updateDoc(ref, { "profile.displayName": displayName, updatedAt: nowTs() });
    return true;
}

/** 전화번호 저장 */
export async function setPhone(uid, phoneE164) {
    const db = getDb();
    const ref = doc(collection(db, USERS), uid);
    await updateDoc(ref, {
        phoneE164,
        phoneVerifiedAt: nowTs(),
        updatedAt: nowTs(),
    });
    return true;
}

/** 로그인 수단 토글/추가 */
export async function setLoginMethod(uid, methodKey, enabled = true) {
    const db = getDb();
    const ref = doc(collection(db, USERS), uid);
    await updateDoc(ref, {
        [`loginMethods.${methodKey}`]: !!enabled,
        updatedAt: nowTs(),
    });
    return true;
}

/** 마지막 로그인 갱신 */
export async function touchLastLogin(uid) {
    const db = getDb();
    const ref = doc(collection(db, USERS), uid);
    await updateDoc(ref, { lastLoginAt: nowTs(), updatedAt: nowTs() });
    return true;
}

/** 소프트 삭제 (복구 가능) */
export async function softDelete(uid) {
    const db = getDb();
    const ref = doc(collection(db, USERS), uid);
    await updateDoc(ref, { active: false, deletedAt: nowTs(), updatedAt: nowTs() });
    return true;
}

/** memberId 링크 (uid → memberId) */
export async function setMemberId(uid, memberId) {
    const db = getDb();
    const ref = doc(collection(db, USERS), uid);
    await updateDoc(ref, { memberId, updatedAt: nowTs() });
    return true;
}

/** 전화번호로 최근 uid 하나 찾기 (단순 조회용) */
export async function getUidByPhone(phoneE164) {
    const db = getDb();
    const q = query(collection(db, USERS), where("phoneE164", "==", phoneE164), limit(1));
    const qs = await getDocs(q);
    return qs.empty ? null : qs.docs[0].id;
}

export default {
    getUser,
    ensureUserDoc,
    updateUser,
    setAgreements,
    setDisplayName,
    setPhone,
    setLoginMethod,
    touchLastLogin,
    softDelete,
    setMemberId,
    getUidByPhone,
};
